import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of, forkJoin } from 'rxjs';
import { Order, CartItem, OrderStatus, DoseType, UserDoseHistory } from '../interfaces/product.interface';
import { OffersConfig } from '../shared/config/offers.config';
import { AuthService } from './auth.service';
import { Firestore, collection, addDoc, updateDoc, doc, getDoc } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root'
})
export class OrderService {
  private ordersSubject = new BehaviorSubject<Order[]>([]);
  public orders$ = this.ordersSubject.asObservable();

  private mockOrders: Order[] = [];
  private readonly ORDERS_COLLECTION = 'orderTable';

  constructor(
    private authService: AuthService,
    private firestore: Firestore
  ) {}

  // Place a new order
  placeOrder(
    cartItems: CartItem[], 
    deliveryAddress: string, 
    paymentMethod: string,
    isComboOrder: boolean = false,
    discountAmount: number = 0,
    paymentDetails?: any,
    deliveryCharges: number = 0
  ): Observable<{ success: boolean; message: string; order?: Order }> {
    const currentUser = this.authService.getCurrentUser();
    if (!currentUser) {
      return of({ success: false, message: 'User not logged in!' });
    }

    const totalAmount = cartItems.reduce((total, item) => total + ((item.price ?? 0) * item.cartQuantity), 0) - discountAmount;
    const doseTypes: DoseType[] = cartItems.map(item => item.doseType);

    // Determine payment status based on payment details
    let paymentStatus: 'pending' | 'completed' | 'failed' = 'pending';
    if (paymentDetails) {
      if (paymentDetails.success) {
        paymentStatus = 'completed';
      } else {
        paymentStatus = 'failed';
      }
    }

    // Convert cart items to plain objects (Firestore compatible) - remove any complex objects
    const serializedProducts = cartItems.map(item => {
      // Extract only serializable fields
      const serialized: any = {
        id: item.id,
        name: item.name,
        description: item.description || '',
        price: item.price ?? 0,
        originalPrice: item.originalPrice, // Save original price for display
        cartQuantity: item.cartQuantity,
        doseNumber: item.doseNumber,
        doseType: item.doseType
      };
      
      // Only add optional fields if they exist
      if (item.selectedColor) serialized.selectedColor = item.selectedColor;
      if (item.image) serialized.image = item.image;
      
      // Save category information for order history
      if (item.selectedCategoryId) serialized.selectedCategoryId = item.selectedCategoryId;
      if (item.selectedCategoryName) serialized.selectedCategoryName = item.selectedCategoryName;
      if (item.selectedCategorySubProducts && item.selectedCategorySubProducts.length > 0) {
        serialized.selectedCategorySubProducts = item.selectedCategorySubProducts;
      }
      
      // Save selected sub-product option information (actual data for DB)
      if (item.selectedSubProductIndex !== undefined && item.selectedSubProductIndex >= 0) {
        serialized.selectedSubProductIndex = item.selectedSubProductIndex;
      }
      if (item.selectedSubProductText) {
        serialized.selectedSubProductText = item.selectedSubProductText; // Actual sub-product data
      }
      
      return serialized;
    });

    const orderData = {
      userId: currentUser.id,
      products: serializedProducts,
      totalAmount,
      discountAmount,
      deliveryCharges,
      status: 'pending',
      orderDate: new Date(), // Firestore will handle Date objects
      deliveryAddress,
      paymentMethod,
      paymentStatus,
      doseTypes,
      isComboOrder,
      // Add payment details if available
      ...(paymentDetails && {
        // Store payment details based on payment method
        ...(paymentMethod === 'razorpay' && {
          razorpayOrderId: paymentDetails.order_id,
          razorpayPaymentId: paymentDetails.payment_id,
          razorpaySignature: paymentDetails.signature,
        }),
        ...(paymentMethod === 'upi' && {
          upiTransactionId: paymentDetails.payment_id,
          upiId: paymentDetails.upi_id,
          upiOrderId: paymentDetails.order_id,
        }),
        // Common payment fields
        paymentAmount: paymentDetails.amount,
        paymentVerified: paymentDetails.verified,
        paymentTimestamp: new Date(),
        // Store courier/delivery partner information
        ...(paymentDetails.courier && {
          selectedCourier: {
            id: paymentDetails.courier.id || paymentDetails.courier.courier_company_id,
            courierId: paymentDetails.courier.courier_company_id,
            courierName: paymentDetails.courier.courier_name,
            deliveryRate: paymentDetails.courier.rate,
            estimatedDeliveryDays: paymentDetails.courier.estimated_delivery_days,
            codAvailable: paymentDetails.courier.cod_available !== false
          }
        })
      }),
      // Add order history
      orderHistory: [{
        timestamp: new Date(),
        status: 'pending',
        notes: 'Order created',
        updatedBy: 'system'
      }]
    };

    // Save to Firebase
    const ordersRef = collection(this.firestore, this.ORDERS_COLLECTION);
    return new Observable(observer => {
      addDoc(ordersRef, orderData).then(docRef => {
        const newOrder: Order = {
          id: docRef.id,
          ...orderData
        };

        // Also update local mock data for backward compatibility
        this.mockOrders.push(newOrder);
        this.ordersSubject.next(this.mockOrders);

        observer.next({ success: true, message: 'Order placed successfully!', order: newOrder });
        observer.complete();
      }).catch(error => {
        observer.next({ success: false, message: 'Failed to save order. Please try again.' });
        observer.complete();
      });
    });
  }

  // Confirm order and start dose period
  confirmOrder(orderId: string): Observable<{ success: boolean; message: string }> {
    // Update both Firebase and local data
    const orderRef = doc(this.firestore, this.ORDERS_COLLECTION, orderId);
    
    return new Observable(observer => {
      getDoc(orderRef).then(orderDoc => {
        if (!orderDoc.exists()) {
          observer.next({ success: false, message: 'Order not found!' });
          observer.complete();
          return;
        }

        const orderData = orderDoc.data();
        const currentHistory = orderData['orderHistory'] || [];
        
        // Update Firebase order
        updateDoc(orderRef, {
          status: 'confirmed',
          paymentStatus: 'completed',
          confirmedDate: new Date(),
          orderHistory: [...currentHistory, {
            timestamp: new Date(),
            status: 'confirmed',
            notes: 'Order confirmed and payment completed',
            updatedBy: 'system'
          }]
        }).then(() => {
          // Update local mock data
          const localOrder = this.mockOrders.find(o => o.id === orderId);
          if (localOrder) {
            localOrder.status = 'confirmed';
            localOrder.paymentStatus = 'completed';
          }

          // NOTE: Dose history will be created when order is marked as DELIVERED
          // This ensures the 30-day countdown starts from the delivery date, not purchase date
          // The dose history creation is now handled in AdminOrderService.updateOrderStatus()
          // when status is changed to 'delivered'

          this.ordersSubject.next(this.mockOrders);
          observer.next({ success: true, message: 'Order confirmed and dose period started!' });
          observer.complete();
        }).catch(error => {
          observer.next({ success: false, message: 'Failed to confirm order. Please try again.' });
          observer.complete();
        });
      }).catch(error => {
        observer.next({ success: false, message: 'Failed to find order.' });
        observer.complete();
      });
    });
  }

  // Get user's order history
  getUserOrders(userId: string): Observable<Order[]> {
    const userOrders = this.mockOrders.filter(o => o.userId === userId);
    return of(userOrders);
  }

  // Get order by ID
  getOrderById(orderId: string): Observable<Order | undefined> {
    const order = this.mockOrders.find(o => o.id === orderId);
    return of(order);
  }

  // Update order status
  updateOrderStatus(orderId: string, status: OrderStatus): Observable<{ success: boolean; message: string }> {
    const order = this.mockOrders.find(o => o.id === orderId);
    if (!order) {
      return of({ success: false, message: 'Order not found!' });
    }

    order.status = status;
    this.ordersSubject.next(this.mockOrders);

    return of({ success: true, message: `Order status updated to ${status}!` });
  }

  // Cancel order (only if pending)
  cancelOrder(orderId: string): Observable<{ success: boolean; message: string }> {
    const order = this.mockOrders.find(o => o.id === orderId);
    if (!order) {
      return of({ success: false, message: 'Order not found!' });
    }

    if (order.status !== 'pending') {
      return of({ success: false, message: 'Order cannot be cancelled!' });
    }

    order.status = 'cancelled';
    this.ordersSubject.next(this.mockOrders);

    return of({ success: true, message: 'Order cancelled successfully!' });
  }

  // Get all orders (for admin)
  getAllOrders(): Observable<Order[]> {
    return this.ordersSubject.asObservable();
  }

  // Check eligibility for the combo offer (doses 1,2,3 present)
  isComboEligible(cartItems: CartItem[]): boolean {
    if (!OffersConfig.comboOffer.enabled) return false;
    const doseSet = new Set<number>();
    for (const item of cartItems) {
      // Only count the three main sequential doses
      if (item.doseNumber >= 1 && item.doseNumber <= 3) {
        doseSet.add(item.doseNumber);
      }
    }
    return doseSet.has(1) && doseSet.has(2) && doseSet.has(3);
  }

  // Calculate combo discount: percentage off subtotal when eligible
  calculateComboDiscount(cartItems: CartItem[]): number {
    if (!OffersConfig.comboOffer.enabled) return 0;

    const subtotal = cartItems.reduce((total, item) => total + ((item.price ?? 0) * item.cartQuantity), 0);
    if (subtotal <= 0) return 0;

    const eligible = this.isComboEligible(cartItems);
    if (!eligible) return 0;

    const discount = (OffersConfig.comboOffer.percentOff / 100) * subtotal;
    return Math.max(0, Math.round(discount));
  }

  // Check if user can purchase specific doses
  canPurchaseDoses(userId: string, doseNumbers: number[]): Observable<{ canPurchase: boolean; message: string }> {
    return new Observable(observer => {
      this.authService.checkDosePurchaseEligibility(userId).subscribe(eligibility => {
        let canPurchase = true;
        let message = '';

        for (const doseNumber of doseNumbers) {
          // First check the new flexible approach
          if (eligibility.canPurchaseDoses && eligibility.canPurchaseDoses[doseNumber] !== undefined) {
            if (!eligibility.canPurchaseDoses[doseNumber]) {
              canPurchase = false;
              message = `Dose ${doseNumber} is not available for purchase.`;
              break;
            }
            continue;
          }

          // Fallback to legacy approach for backward compatibility
          switch (doseNumber) {
            case 1:
              if (!eligibility.canPurchaseDose1) {
                canPurchase = false;
                message = 'Green dose (Dose 1) is not available for purchase.';
              }
              break;
            case 2:
              if (!eligibility.canPurchaseDose2) {
                canPurchase = false;
                message = 'Yellow dose (Dose 2) requires Green dose to be completed first.';
              }
              break;
            case 3:
              if (!eligibility.canPurchaseDose3) {
                canPurchase = false;
                message = 'Red dose (Dose 3) requires Green and Yellow doses to be completed first.';
              }
              break;
            default:
              // For dose numbers beyond 3, allow only if 1-3 are completed and no active dose
              const completed = eligibility.completedDoses || [];
              const has1 = completed.includes('green');
              const has2 = completed.includes('yellow');
              const has3 = completed.includes('red');
              if (!(has1 && has2 && has3 && !eligibility.currentActiveDose)) {
                canPurchase = false;
                message = `Dose ${doseNumber} requires previous doses to be completed first.`;
              }
              break;
          }

          if (!canPurchase) break;
        }

        if (eligibility.currentActiveDose) {
          canPurchase = false;
          message = `You have an active ${eligibility.currentActiveDose.doseType} dose. Complete it before purchasing new doses.`;
        }

        if (canPurchase) {
          message = 'All selected doses are available for purchase.';
        }

        observer.next({ canPurchase, message });
        observer.complete();
      });
    });
  }

  // Get order statistics (for admin)
  getOrderStatistics(): Observable<{
    totalOrders: number;
    pendingOrders: number;
    completedOrders: number;
    totalRevenue: number;
    popularDose: string;
  }> {
    const totalOrders = this.mockOrders.length;
    const pendingOrders = this.mockOrders.filter(o => o.status === 'pending').length;
    const completedOrders = this.mockOrders.filter(o => o.status === 'delivered').length;
    const totalRevenue = this.mockOrders
      .filter(o => o.paymentStatus === 'completed')
      .reduce((total, order) => total + order.totalAmount, 0);

    // Find most popular dose
    const doseCount: { [key: string]: number } = {};
    this.mockOrders.forEach(order => {
      order.doseTypes.forEach(dose => {
        doseCount[dose] = (doseCount[dose] || 0) + 1;
      });
    });

    // If no doses found, default to legacy types
    if (Object.keys(doseCount).length === 0) {
      doseCount['green'] = 0;
      doseCount['yellow'] = 0;
      doseCount['red'] = 0;
    }

    const popularDose = Object.entries(doseCount).reduce((a, b) => 
      doseCount[a[0]] > doseCount[b[0]] ? a : b
    )[0];

    return of({
      totalOrders,
      pendingOrders,
      completedOrders,
      totalRevenue,
      popularDose
    });
  }
}
